"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AutomationRobotRunner = void 0;
const automation_robot_runner_interface_1 = require("./interface/automation_robot_runner.interface");
const input_parser_1 = require("./input_parser");
const magic_variable_parser_1 = require("./magic_variable/magic_variable_parser");
const sys_functions_1 = require("./magic_variable/sys_functions");
const lodash_1 = require("lodash");
/**
 * handle workflow execution
 */
class AutomationRobotRunner extends automation_robot_runner_interface_1.IAutomationRobotRunner {
    constructor(reqMethods) {
        super();
        // the reqMethods is used to call api of other service, implement by the host of runner(room-server)
        this.reqMethods = reqMethods;
        this.inputParser = this.initInputParser();
    }
    initInputParser() {
        // functions below are used to parse input of action, you can add any function you want at `./magic_variable/sys_functions`
        // then add it into sysFunctions array. just like standard lib for programming language
        const sysFunctions = [sys_functions_1.length, sys_functions_1.flatten, sys_functions_1.getNodeOutput, sys_functions_1.getObjectProperty, sys_functions_1.concatString, sys_functions_1.concatParagraph, sys_functions_1.newArray, sys_functions_1.newObject, sys_functions_1.JSONStringify];
        const parser = new magic_variable_parser_1.MagicVariableParser(sysFunctions);
        return new input_parser_1.InputParser(parser);
    }
    run(robotTask) {
        return __awaiter(this, void 0, void 0, function* () {
            const robot = yield this.reqMethods.getRobotById(robotTask.robotId);
            const globalContext = this.initRuntimeContext(robotTask, robot);
            const entryActionId = globalContext.robot.entryActionId;
            yield this.executeAction(entryActionId, globalContext);
        });
    }
    validateActionInput(_actionType, _input) {
        // TODO: implement json schema validation
        return true;
    }
    validateActionOutput(_actionType, _output) {
        // TODO: implement json schema validation
        return true;
    }
    initRuntimeContext(robotTask, robot) {
        return {
            robot: robot,
            taskId: robotTask.taskId,
            executedNodeIds: [robot.triggerId],
            currentNodeId: robot.triggerId,
            context: {
                [robot.triggerId]: {
                    typeId: robot.triggerTypeId,
                    input: robotTask.triggerInput,
                    output: robotTask.triggerOutput,
                },
            },
            isDone: false,
            success: true,
        };
    }
    executeAction(actionId, globalContext) {
        return __awaiter(this, void 0, void 0, function* () {
            // console.log('globalContext', globalContext, JSON.stringify(globalContext.context));
            globalContext.currentNodeId = actionId;
            const start = new Date().getTime();
            // get instance of the action by id
            const actionInstance = globalContext.robot.actionsById[actionId];
            // get type of the action
            const actionType = globalContext.robot.actionTypesById[actionInstance.typeId];
            // TODO: validate input
            // if (this.validateActionInput(actionType, actionRuntimeInput)) {}
            let output;
            const errorStacks = [];
            let nextActionId;
            // the input of action may have dynamic value, so we need to parse it
            let actionRuntimeInput;
            try {
                try {
                    actionRuntimeInput = this.getRuntimeActionInput(actionId, globalContext);
                }
                catch (error) {
                    console.log(`actionId:${actionId} actionType:${JSON.stringify((0, lodash_1.omit)(actionType, 'inputJSONSchema', 'outputJSONSchema'))} AutomationRobotRunner:executeAction:getRuntimeActionInput error`, error);
                    throw new Error('action input is invalid');
                }
                if (!this.validateActionInput(actionType, actionRuntimeInput)) {
                    console.log(`actionId:${actionId} actionType:${JSON.stringify((0, lodash_1.omit)(actionType, 'inputJSONSchema', 'outputJSONSchema'))} AutomationRobotRunner:executeAction:validateActionInput failed`);
                    throw new Error('action input is invalid');
                }
                // TODO: push task to queue, to ensure the order of execution
                try {
                    output = yield this.reqMethods.requestActionOutput(actionRuntimeInput, actionType);
                }
                catch (error) {
                    // execute action failed, most likely because of network error
                    console.log(`actionId:${actionId} actionType:${JSON.stringify((0, lodash_1.omit)(actionType, 'inputJSONSchema', 'outputJSONSchema'))} AutomationRobotRunner:executeAction:requestActionOutput error`, error);
                    throw new Error(`action execute failed. error message: ${error.message}`);
                }
                nextActionId = actionInstance.nextActionId;
                if (output && !output.success) {
                    errorStacks.push(...output.data.errors);
                    // when some output of action is failed, we should stop the execution, done but failed
                    globalContext.isDone = true;
                    globalContext.success = false;
                }
            }
            catch (error) {
                errorStacks.push({
                    message: error.message,
                });
                // unexpected error, we should stop the execution, done but failed
                globalContext.isDone = true;
                globalContext.success = false;
            }
            finally {
                const end = new Date().getTime();
                globalContext.executedNodeIds.push(globalContext.currentNodeId);
                globalContext.context[actionId] = {
                    typeId: actionType.id,
                    input: actionRuntimeInput,
                    output: output === null || output === void 0 ? void 0 : output.data,
                    startAt: start,
                    endAt: end,
                    errorStacks,
                };
            }
            if (globalContext.isDone) {
                yield this.reportResult(globalContext.taskId, globalContext);
            }
            else if (nextActionId) {
                yield this.executeAction(nextActionId, globalContext);
            }
            else {
                yield this.reportResult(globalContext.taskId, globalContext);
            }
            // }
        });
    }
    getRuntimeActionInput(actionId, globalContext) {
        return this.inputParser.render(globalContext.robot.actionsById[actionId].input, globalContext);
    }
    reportResult(taskId, globalContext) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.reqMethods.reportResult(taskId, globalContext.success, {
                executedNodeIds: globalContext.executedNodeIds,
                nodeByIds: globalContext.context,
            });
        });
    }
}
exports.AutomationRobotRunner = AutomationRobotRunner;
//# sourceMappingURL=automation_robot_runner.js.map